export default {
  header: {
    title: 'Manage dashboard access',
    invite_button: 'Invite someone',
    requires_admin: 'Requires Admin role',
  },
  tabs: {
    users: 'USERS',
    roles: 'ROLES & PERMISSIONS',
  },
  roles_tab: {
    title: 'Roles',
    create_new_role: 'Create new role',
    assignable_roles: 'Assignable roles',
    role_templates: 'Role templates',
    role_templates_subtitle:
      'Cannot be assigned or edited. Use as a starting point for custom roles.',
    edit: 'Edit',
    view: 'View',
    user_count: '%{count} user',
    user_count_plural: '%{count} users',
    actions_for: 'Actions for %{roleName}',
    delete: 'Delete',
    requires_admin: 'Requires Admin role',
    badge: {
      standard: 'STANDARD',
      template: 'TEMPLATE',
      global: 'GLOBAL',
      local: 'LOCAL',
    },
  },
  create_modal: {
    title: 'Create new role',
    from_scratch: 'Start from scratch',
    from_scratch_description: 'Create a blank role with default permissions',
    from_template: 'Start from an existing role or template',
    from_template_description: 'Copy permissions from another role or template',
    choose_role_title: 'Choose a role to start from',
  },
  delete_modal: {
    title: 'Delete this role',
    description:
      'Are you sure you want to delete the role "%{roleName}"? This action cannot be undone.',
    has_users:
      'This role cannot be deleted because it has %{count} users assigned to it. Please reassign those users to a different role first.',
    cancel: 'Cancel',
    delete: 'Delete',
    delete_error: 'Failed to delete role. Please try again.',
  },
  edit_role: {
    configure: 'CONFIGURE',
    title: 'Permissions',
    cancel: 'Exit without saving',
    save: 'Save settings',
    save_success_created: 'Role created successfully.',
    save_success_updated: 'Role updated successfully.',
    save_error: 'Failed to save role. Please try again.',
    edit_role_name: 'Edit role name',
    role_name_label: 'Role name',
    role_name_placeholder: 'e.g. Regional Manager',
    role_name_required: 'Role name is required',
    new_role: 'New role',
    badge: {
      standard: 'STANDARD',
      template: 'TEMPLATE',
    },
    admin_required_warning: 'Admin access is required to edit roles.',
    merchant_users_locked:
      'Only the Admin role can manage user access & roles.',
    info_banner: {
      title: 'Location access',
      description:
        'Roles apply to all locations by default. To restrict access, assign specific locations to the user.',
    },
    subsections: {
      campaigns: {
        title: 'Campaigns',
        description:
          'Controls access to campaign content, collaboration, and approvals.',
      },
      segments: {
        title: 'Segments',
        description: 'Controls access to audience segments and collaboration.',
      },
    },
    campaign_warning_banner:
      'Campaign permissions update in progress. We\u2019re improving how campaign permissions work with custom roles. In the meantime, if a user is unable to access campaigns as expected, grant them \u201cView and Edit\u201d permissions for \u201cAll Campaigns\u201d as a temporary workaround. If you have any questions, email our team at success@thanx.com.',
    permission_levels: {
      view_and_edit: 'View and edit',
      can_view: 'Can view',
      no_access: 'No access',
    },
    permission_descriptions: {
      // Marketing
      campaigns:
        'View: see your own campaigns and performance reports. Edit: create, modify, and send your own campaigns.',
      campaign_collaboration:
        'View: see campaigns created by other users. Edit: modify and manage campaigns created by other users.',
      campaign_approvals:
        'View: see pending campaign approvals. Edit: review and approve campaigns submitted for approval.',
      segments:
        'View: see your own segments. Edit: create, modify, and delete your own segments.',
      segment_collaboration:
        'View: see segments created by other users. Edit: modify and delete segments created by other users.',
      smart_links:
        'View: see smart links. Edit: create, modify, and manage smart links.',
      // Customers
      feedback:
        'View: see customer feedback and reviews. Edit: respond to and manage feedback.',
      members:
        'View: search and view customer profiles. Edit: modify customer data and access support tools.',
      // Dashboard Management
      merchant_users:
        'View: see dashboard team members. Edit: invite, modify, and deactivate team members.',
      billing:
        'View: see billing and subscription details. Edit: manage billing settings and subscriptions.',
      merchant_settings:
        'View: see merchant settings, legal documents, compliance status, and RecoveryAI. Edit: update merchant settings, manage legal documents, send support requests, and configure RecoveryAI.',
      // Program Configuration
      loyalty:
        'View: see points, tiers, multipliers, and other loyalty settings. Edit: modify points settings, manage tiers, and configure loyalty options.',
      reward_templates:
        'View: see offer templates and reward activity. Edit: create, modify, and delete offer templates.',
      tiers_program: 'View: see tier levels, requirements, and configuration.',
      points_multipliers:
        'View: see points multipliers and promotions. Edit: create, modify, and delete points multipliers.',
      reward_issuance:
        'View: see reward issuance activity. Edit: grant, reissue, and retire rewards for customers.',
      reward_redemptions: 'View: see reward redemption programs and activity.',
      // Games
      games: 'View: see games. Edit: create, modify, and manage games.',
      // Digital Experiences
      locations:
        'View: see locations. Edit: create, modify, and manage locations and manage location settings.',
      ordering_configuration:
        'View: see ordering settings. Edit: modify ordering configuration and error messages.',
      content_management:
        'View: see content management settings. Edit: modify brand content, home page, navigation, and other digital experience settings.',
      app_configuration:
        'View: see mobile app themes, navigation, and landing page settings. Edit: modify app themes, navigation tabs, onboarding slideshow, and landing page content.',
      digital_experiences:
        'View: see receipt configuration and web experience links. Edit: modify receipt settings and manage web links.',
      // Reporting
      reporting:
        'View overall business performance as totals — no ability to see how individual locations are performing.',
      reporting_location:
        'View performance data broken out by individual location. Includes all locations, not just locations assigned to the user.',
    },
    location_restriction_notes: {
      toggle_label:
        'How this setting affects users with location-specific access',
      // Marketing
      campaigns:
        'When a user is limited to specific locations, any campaign they create will automatically target only customers who have shopped at those locations. Rewards earned from their campaigns can also only be redeemed at those locations. They cannot broaden a campaign beyond their assigned locations.',
      targeted_campaigns:
        "Same as Campaigns — targeting and redemption are automatically restricted to the user's assigned locations.",
      automated_campaigns:
        "Same as Campaigns — targeting and redemption are automatically restricted to the user's assigned locations.",
      segments:
        "When a user is limited to specific locations, any segment they create (including through SegmentAI) will automatically include only customers who have made purchases at their locations. They won't see this filter — it's applied behind the scenes whenever the segment is used.",
      // Customers
      feedback:
        "When a user is limited to specific locations, they will only see feedback from customers who made purchases at their locations. They can respond to that feedback, but feedback from other locations won't appear at all.",
      members:
        "Location restrictions do not limit which customer profiles are visible. A location-restricted user can view any customer's full profile, including their complete purchase history across all locations.",
      // Digital Experiences
      locations:
        "When a user is limited to specific locations, they will only see their assigned locations in any location list or picker throughout the dashboard. Other locations won't be visible.",
      ordering_configuration:
        'When a user is limited to specific locations, they can only view and edit ordering settings for their assigned locations.',
      // Reporting
      reporting_location:
        'When a user is limited to specific locations, dashboard metrics (like audience size and visit patterns) are automatically filtered to reflect only activity at their locations. Note: Embedded Insights Labs reports are not yet location-filtered — giving this permission to a location-restricted user will allow them to explore reports that expose location-specific performance across all locations.',
    },
  },
}
